/* global API_GATEWAY_TOKEN */
import React, { useEffect, useState, useContext } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Link } from 'react-router-dom';
import { Select, Button, Card, Space, Typography, Alert, Switch, message } from 'antd';
import { PlusOutlined, TagsOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import download from 'downloadjs';
import Auth from 'Lib/auth';
import ContextSnackbar from 'Components/Snackbar';
import { HeadingTwo } from 'Components/Headings';
import { subRouteCodes as SUB_ROUTES } from 'Constants/routes';
import { VENUES } from 'Queries/SuperAdmin';
import { DISCOUNT_CODES_BY_VENUE } from 'Queries/SuperAdmin/DiscountCodes';
import DiscountCodesTable from 'Components/DiscountCodes/DiscountCodesTable';
import { TOGGLE_DISCOUNT_CODES_FOR_RENTERS } from 'Mutations/ToggleDiscountCodesForRenters';
import { Report } from 'Constants/reportType';
import { UserContext } from 'Store/UserContext';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';

const { Option } = Select;
const { Text, Title } = Typography;

const PageContainer = styled.div`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }
`;

const PageHeader = styled.div`
  text-align: left;
  width: 100%;
  border-bottom: 1px solid rgb(200, 214, 229);
  &&& {
    margin: 65px 0 25px;
  }
`;

const HeaderWrapper = styled.div`
  margin: 20px 0px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageBody = styled.div`
  text-align: left;
  width: 100%;
  &&& {
    margin: 0;
    padding: 10px 20px;
  }
`;

const VenueSelector = styled(Card)`
  margin-bottom: 24px;

  .venue-select-label {
    text-transform: uppercase;
    font-family: IBMPlexSans-Bold;
    font-size: 12px;
    font-weight: 600;
    color: rgb(79, 93, 109);
    margin-bottom: 8px;
    display: block;
  }

  .ant-select {
    min-width: 300px;
  }
`;

const InfoAlert = styled(Alert)`
  margin-bottom: 24px;
`;

const CreateButton = styled(Button)`
  font-family: IBMPlexSans-SemiBold;
  font-weight: 600;
`;

const StatsCard = styled(Card)`
  margin-bottom: 24px;

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 24px;
  }

  .stat-item {
    text-align: center;
  }

  .stat-value {
    font-size: 32px;
    font-weight: 700;
    color: rgb(51, 63, 72);
    display: block;
  }

  .stat-label {
    font-size: 12px;
    color: rgb(100, 116, 139);
    text-transform: uppercase;
    margin-top: 4px;
  }
`;

const RenterSettingsCard = styled(Card)`
  margin-bottom: 24px;

  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .setting-info {
    flex: 1;
  }

  .setting-title {
    font-family: IBMPlexSans-SemiBold;
    font-size: 14px;
    font-weight: 600;
    color: rgb(51, 63, 72);
    margin-bottom: 4px;
  }

  .setting-description {
    font-size: 13px;
    color: rgb(100, 116, 139);
  }
`;

const ReportCard = styled(Card)`
  margin-bottom: 24px;

  .report-selectors {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .selector-group {
    flex: 1;
    min-width: 250px;
  }

  .selector-label {
    text-transform: uppercase;
    font-family: IBMPlexSans-Bold;
    font-size: 12px;
    font-weight: 600;
    color: rgb(79, 93, 109);
    margin-bottom: 8px;
    display: block;
  }
`;

const DiscountCodes = () => {
  const { user } = useContext(UserContext);
  const API_URL = process.env.REACT_APP_API_URL || '';
  const [messageApi, contextHolder] = message.useMessage();
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const { data: venuesData, loading: venuesLoading, error: venuesError, refetch: refetchVenues } = useQuery(VENUES, {
    fetchPolicy: 'network-only'
  });

  const { data: discountCodesData, loading: discountCodesLoading, error: discountCodesError, refetch: refetchDiscountCodes } = useQuery(
    DISCOUNT_CODES_BY_VENUE,
    {
      variables: { venueId: selectedVenue?.id },
      skip: !selectedVenue,
      fetchPolicy: 'network-only'
    }
  );

  const [toggleDiscountCodesForRenters] = useMutation(TOGGLE_DISCOUNT_CODES_FOR_RENTERS, {
    onCompleted: data => {
      if (data.toggleDiscountCodesForRenters.success) {
        // Update the selected venue in state
        const updatedVenue = data.toggleDiscountCodesForRenters.venue;
        setSelectedVenue(prev => ({ ...prev, discountCodesForRenters: updatedVenue.discountCodesForRenters }));
        // Also update in venues list
        setVenues(prev => prev.map(v => (v.id === updatedVenue.id ? { ...v, discountCodesForRenters: updatedVenue.discountCodesForRenters } : v)));
      }
      setToggleLoading(false);
    },
    onError: () => {
      setToggleLoading(false);
    }
  });

  useEffect(() => {
    if (venuesData) {
      const sortedVenues = [...venuesData.venues].sort((a, b) => +a.id - +b.id);
      setVenues(sortedVenues);
    }
  }, [venuesData]);

  const handleVenueChange = venueId => {
    const venue = venues.find(v => v.id === venueId);
    setSelectedVenue(venue);
    setSelectedEvent(null); // Reset event selection when venue changes
  };

  const handleEventChange = eventId => {
    const event = selectedVenue?.events?.find(e => e.id === eventId);
    setSelectedEvent(event);
  };

  const handleDownloadReport = () => {
    if (!selectedVenue || !selectedEvent) return;

    setLoadingReport(true);
    const builtBody = {
      reportType: Report.DEFERRED_FEES,
      venueId: selectedVenue.id,
      eventId: selectedEvent.id,
      userId: user.id
    };

    const token = Auth.getToken();
    const PENDING_MESSAGE = 'too large';
    const payload = JSON.parse(JSON.stringify(builtBody));

    fetch(`${API_URL}/admin/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        auth: token ? `Bearer ${token}` : '',
        token: API_GATEWAY_TOKEN
      },
      mode: 'cors',
      body: JSON.stringify(payload)
    })
      .then(async response => {
        if (response.status === 204) {
          messageApi.open({
            type: 'error',
            content: 'No records found'
          });
          throw new Error('');
        } else if (response.status === 202) {
          return await response.text();
        } else return response.blob();
      })
      .then(blob => {
        if (typeof blob === 'string' && blob.includes(PENDING_MESSAGE)) {
          messageApi.open({
            type: 'warning',
            content: blob
          });
        } else download(blob, `${selectedEvent.name} - ${selectedVenue.name} - deferred-fees_report.xlsx`);
      })
      .catch(err => {
        if (err.message) {
          messageApi.open({
            type: 'error',
            content: 'There was an error with the report'
          });
          console.log(err);
        }
      })
      .finally(() => {
        setLoadingReport(false);
      });
  };

  const handleToggleRenterAccess = async checked => {
    if (!selectedVenue) return;

    setToggleLoading(true);
    await toggleDiscountCodesForRenters({
      variables: {
        input: {
          venueId: selectedVenue.id,
          discountCodesForRenters: checked
        }
      }
    });
  };

  const discountCodes = discountCodesData?.discountCodesByVenueId?.discountCodes || [];

  // Calculate statistics
  const stats = {
    total: discountCodes.length,
    active: discountCodes.filter(dc => dc.isActive).length,
    inactive: discountCodes.filter(dc => !dc.isActive).length,
    totalUses: discountCodes.reduce((sum, dc) => sum + (dc.currentUses || 0), 0)
  };

  return (
    <>
      {contextHolder}
      <PageContainer>
        <ContextSnackbar />
        <PageHeader>
          <HeaderWrapper>
            <HeadingTwo label="DISCOUNT CODES" />
            <Link to={SUB_ROUTES.SUPER_ADMIN.DISCOUNT_CODES_CREATE} style={{ textDecoration: 'none' }}>
              <CreateButton type="primary" icon={<PlusOutlined />} size="large">
                CREATE DISCOUNT CODE
              </CreateButton>
            </Link>
          </HeaderWrapper>
        </PageHeader>
      </PageContainer>

      <PageBody>
        <InfoAlert
          message="Discount Codes Management"
          description="Create and manage discount codes for venues. Codes can be percentage-based or fixed amounts, with optional usage limits and validity periods. Once created, most fields are immutable to maintain historical accuracy."
          type="info"
          icon={<TagsOutlined />}
          showIcon
        />

        <VenueSelector>
          <label className="venue-select-label">Select Venue</label>
          <Select
            showSearch
            placeholder="Choose a venue to view its discount codes"
            onChange={handleVenueChange}
            value={selectedVenue?.id}
            loading={venuesLoading}
            style={{ width: '100%', maxWidth: 500 }}
            optionFilterProp="children"
            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
            {venues.map(venue => (
              <Option key={venue.id} value={venue.id}>
                ID: {venue.id} - {venue.name}
              </Option>
            ))}
          </Select>

          {venuesError && <Alert message="Error loading venues" description={venuesError.message} type="error" style={{ marginTop: 16 }} />}
        </VenueSelector>

        {selectedVenue && (
          <>
            <RenterSettingsCard title="Renter Discount Code Access">
              <div className="setting-row">
                <div className="setting-info">
                  <div className="setting-title">Enable Discount Codes for Renters</div>
                  <div className="setting-description">When enabled, renters can apply discount codes you share with them during checkout</div>
                </div>
                <Switch checked={selectedVenue.discountCodesForRenters || false} onChange={handleToggleRenterAccess} loading={toggleLoading} />
              </div>
            </RenterSettingsCard>

            <ReportCard title="Deferred Fees Report">
              <div className="report-selectors">
                <div className="selector-group">
                  <label className="selector-label">Select Event</label>
                  <Select
                    showSearch
                    placeholder="Choose an event"
                    onChange={handleEventChange}
                    value={selectedEvent?.id}
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {selectedVenue?.events
                      ?.slice()
                      .sort((a, b) => +b.id - +a.id)
                      .map(event => (
                        <Option key={event.id} value={event.id}>
                          ID: {event.id} - {event.name}
                        </Option>
                      ))}
                  </Select>
                </div>
                {selectedEvent && (
                  <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadReport} loading={loadingReport} size="large">
                    Download Report
                  </Button>
                )}
              </div>
            </ReportCard>

            {stats.total > 0 && (
              <StatsCard title={`Discount Codes Statistics for ${selectedVenue.name}`}>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{stats.total}</span>
                    <div className="stat-label">Total Codes</div>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#52c41a' }}>
                      {stats.active}
                    </span>
                    <div className="stat-label">Active Codes</div>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#d9d9d9' }}>
                      {stats.inactive}
                    </span>
                    <div className="stat-label">Inactive Codes</div>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: '#1890ff' }}>
                      {stats.totalUses}
                    </span>
                    <div className="stat-label">Total Uses</div>
                  </div>
                </div>
              </StatsCard>
            )}

            <DiscountCodesTable discountCodes={discountCodes} loading={discountCodesLoading} refetch={refetchDiscountCodes} venueId={selectedVenue.id} />

            {discountCodesError && (
              <Alert message="Error loading discount codes" description={discountCodesError.message} type="error" style={{ marginTop: 16 }} />
            )}
          </>
        )}

        {!selectedVenue && (
          <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
            <TagsOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} style={{ color: 'rgb(100, 116, 139)' }}>
              Select a venue to view and manage discount codes
            </Title>
            <Text style={{ color: 'rgb(148, 163, 184)' }}>Choose a venue from the dropdown above to see its discount codes</Text>
          </Card>
        )}
      </PageBody>
    </>
  );
};

export default DiscountCodes;
